// pages/EncuestasCategorias.js
import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch, Space,
  Typography, message, Tag, Tooltip, Row, Col, Popconfirm,
  Badge, Statistic, InputNumber, Card
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  AppstoreOutlined, FolderOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;

const EncuestasCategorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentCategoria, setCurrentCategoria] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 120,
      sorter: (a, b) => (a.Codigo || '').localeCompare(b.Codigo || ''),
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      render: (c) => <Tag color="blue" style={{ fontWeight: 'bold' }}>{c}</Tag>,
      fixed: 'left',
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 250,
      sorter: (a, b) => (a.Descripcion || '').localeCompare(b.Descripcion || ''),
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      render: (d) => <Text strong>{d}</Text>,
    },
    {
      title: 'Orden',
      dataIndex: 'Orden',
      key: 'Orden',
      width: 100,
      sorter: (a, b) => (a.Orden || 0) - (b.Orden || 0),
      sortOrder: sortedInfo.columnKey === 'Orden' && sortedInfo.order,
      render: (o) => <Tag color="geekblue">{o}</Tag>,
    },
    {
      title: 'Activo',
      dataIndex: 'Activo',
      key: 'Activo',
      width: 100,
      render: (activo) => activo
        ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
        : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />,
      filters: [{ text: 'Sí', value: true }, { text: 'No', value: false }],
      filteredValue: filteredInfo.Activo || null,
      onFilter: (value, record) => record.Activo === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button type="primary" icon={<EditOutlined />} size="small"
              onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar categoría?"
            description="Se eliminará permanentemente"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí" cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => { fetchCategorias(); }, []);

  const fetchCategorias = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = {
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'Orden',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      
      const response = await axios.get(`${BASE_URL}/api/encuestas/categorias`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });
      
      setCategorias(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando categorías:', error);
      message.error('Error al cargar las categorías');
      setCategorias([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleCreate = () => {
    setModalType('create');
    setCurrentCategoria(null);
    form.resetFields();
    form.setFieldsValue({ Activo: true, Orden: 0 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentCategoria(record);
    form.setFieldsValue({
      Codigo: record.Codigo,
      Descripcion: record.Descripcion,
      Orden: record.Orden,
      Activo: record.Activo,
    });
    setModalVisible(true);
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/encuestas/categorias/${codigo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategorias(categorias.filter(c => c.Codigo !== codigo));
      message.success('Categoría eliminada correctamente');
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar la categoría');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      if (modalType === 'create') {
        await axios.post(`${BASE_URL}/api/encuestas/categorias`, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Categoría creada correctamente');
      } else {
        await axios.put(`${BASE_URL}/api/encuestas/categorias/${currentCategoria.Codigo}`, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Categoría actualizada correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchCargos();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  const fetchCargos = () => fetchCategorias();

  const handleSearch = () => {
    setSearchLoading(true);
    fetchCategorias();
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchCategorias();
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por código o descripción..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
          prefix={<SearchOutlined />}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}
          loading={searchLoading}>Buscar</Button>
        <Button icon={<ReloadOutlined />} onClick={handleResetSearch}>Restablecer</Button>
      </Space>
    </div>
  );

  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={8}>
        <Statistic title="Total Categorías" value={categorias.length}
          prefix={<FolderOutlined />} valueStyle={{ color: '#1890ff' }} />
      </Col>
      <Col span={8}>
        <Statistic title="Activas" value={categorias.filter(c => c.Activo).length}
          suffix={`/ ${categorias.length}`} prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#52c41a' }} />
      </Col>
      <Col span={8}>
        <Statistic title="Inactivas" value={categorias.filter(c => !c.Activo).length}
          prefix={<CloseCircleOutlined />} valueStyle={{ color: '#ff4d4f' }} />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Categorías de Encuestas"
        subtitle={`${categorias.length} categorías registradas`}
        icon={<AppstoreOutlined />}
        loading={loading}
        onRefresh={() => fetchCategorias()}
        onCreate={handleCreate}
        badgeCount={categorias.length}
        badgeStatus="processing"
        cardStyle={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
      >
        {estadisticas}
        {searchBar}
        <Table
          columns={columns}
          dataSource={categorias}
          rowKey="Codigo"
          loading={loading}
          scroll={{ x: 900, y: 600 }}
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          onChange={(pagination, filters, sorter) => {
            setFilteredInfo(filters);
            setSortedInfo(sorter);
          }}
          locale={{ emptyText: 'No se encontraron categorías' }}
        />
      </PageContainer>

      <Modal
        title={modalType === 'create' ? 'Nueva categoría' : 'Editar categoría'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 40 }}
      >
        <Form form={form} layout="vertical" initialValues={{ Activo: true, Orden: 0 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="Codigo" label="Código"
                rules={[{ required: true, message: 'Ingrese el código', max: 10 }]}
                tooltip="Código único (ej: LIMP, ATEN)">
                <Input placeholder="Ej: LIMP" maxLength={10}
                  disabled={modalType === 'edit'} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="Descripcion" label="Descripción"
                rules={[{ required: true, message: 'Ingrese la descripción', max: 100 }]}>
                <Input placeholder="Ej: Limpieza" maxLength={100} showCount />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="Orden" label="Orden" tooltip="Orden de aparición">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="Activo" label="Activo" valuePropName="checked">
                <Switch checkedChildren="Sí" unCheckedChildren="No" style={{ minWidth: 80 }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default EncuestasCategorias;